import React from "react";
import { Check, Undo2, Pen, Highlighter, Sparkles, Eraser } from "lucide-react";
import { VSlider } from "./StoryEditorBars";
import { DRAW_PALETTE, DRAW_TOOLS, strokeWidthPx } from "./storyEditorUtils";

const ICONS = { pen: Pen, marker: Highlighter, neon: Sparkles, eraser: Eraser };

// Outils de dessin : stylo / marqueur / néon / gomme, épaisseur (curseur vertical), couleurs, annuler, terminé.
export const StoryDrawTools = ({ draw, setDraw, canUndo, onUndo, onDone, t }) => {
  const labels = { pen: t("story.draw_pen", "Stylo"), marker: t("story.draw_marker", "Marqueur"), neon: t("story.draw_neon", "Néon"), eraser: t("story.draw_eraser", "Gomme") };
  const w = strokeWidthPx(draw.tool, draw.thickness);
  return (
    <>
      <div className="absolute top-0 inset-x-0 z-30 px-3 bg-gradient-to-b from-black/70 to-transparent pb-4" style={{ paddingTop: "calc(var(--sa-top, 0px) + 10px)" }} data-testid="story-draw-tools">
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={onUndo} disabled={!canUndo} aria-label={t("story.undo", "Annuler le trait")} className="h-10 w-10 grid place-items-center rounded-full bg-black/40 text-white disabled:opacity-40" data-testid="story-draw-undo"><Undo2 size={20} /></button>
          <div className="flex-1 min-w-0 mx-2 flex items-center gap-1.5 overflow-x-auto hide-scrollbar [&>*:first-child]:ml-auto [&>*:last-child]:mr-auto">
            {DRAW_TOOLS.map(({ key }) => {
              const Icon = ICONS[key];
              const on = draw.tool === key;
              return (
                <button key={key} type="button" onClick={() => setDraw((d) => ({ ...d, tool: key }))} aria-pressed={on} aria-label={labels[key]} className={`shrink-0 h-9 rounded-full text-xs font-semibold flex items-center gap-1.5 border transition-colors ${on ? "pl-2.5 pr-3" : "w-9 justify-center"}`} style={{ background: on ? "#fff" : "rgba(0,0,0,.4)", color: on ? "#111" : "#fff", borderColor: on ? "#fff" : "rgba(255,255,255,.25)" }} data-testid={`story-draw-tool-${key}`}>
                  <Icon size={15} /> {on && labels[key]}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={onDone} className="h-10 px-4 rounded-full text-white text-sm font-bold flex items-center gap-1 active:scale-95 transition-transform shrink-0" style={{ background: "var(--yalola-primary)" }} data-testid="story-draw-done">
            <Check size={16} /> {t("story.done", "Terminé")}
          </button>
        </div>
      </div>

      <div className="absolute left-1.5 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-2" aria-label={t("story.thickness", "Épaisseur")}>
        <span className="rounded-full bg-white" style={{ width: Math.max(4, Math.min(28, w)), height: Math.max(4, Math.min(28, w)), boxShadow: "0 0 0 1px rgba(0,0,0,.4)" }} data-testid="story-draw-width-preview" />
        <VSlider value={draw.thickness} onChange={(v) => setDraw((d) => ({ ...d, thickness: v }))} testId="story-draw-thickness" />
      </div>

      {draw.tool !== "eraser" && (
        <div className="absolute inset-x-0 z-30 px-4 flex items-center gap-2.5 overflow-x-auto hide-scrollbar" style={{ bottom: "calc(var(--sa-bottom, 0px) + 18px)" }} data-testid="story-draw-colors">
          {DRAW_PALETTE.map((c) => (
            <button key={c} type="button" onClick={() => setDraw((d) => ({ ...d, color: c }))} aria-label={c} className="h-8 w-8 shrink-0 rounded-full border-2 transition-transform active:scale-90" style={{ background: c, borderColor: draw.color === c ? "var(--yalola-primary)" : "rgba(255,255,255,.7)", transform: draw.color === c ? "scale(1.15)" : "none" }} data-testid={`story-draw-color-${c.replace("#", "")}`} />
          ))}
        </div>
      )}
    </>
  );
};
